import {
  Alert,
  Backdrop,
  Button,
  CircularProgress,
  Grid,
  Stack,
  Typography,
} from '@mui/material';
import * as Yup from 'yup';

import 'yup-phone';
import React, { useEffect, useState } from 'react';

import { Box } from '@mui/system';
import { useFormik } from 'formik';
import { useNavigate, useParams } from 'react-router-dom';
import { Pages } from 'src/common/constants/Pages';
import { useUserRole } from 'src/common/hooks/useUserRole';
import { openBillingPortal } from 'src/common/utils/Organization';

import {
  useSaveOrganizationMutation,
  useUpdateOrganizationMutation,
  useGetOrganizationMutation,
  useActivateOrgSubscriptionMutation,
} from '../../../common/apis/AdminApi';
import { ReactComponent as ChevronLeftIconBlue } from '../../../common/assets/icons/chevron_left_icon_blue.svg';
import { ReactComponent as LocationIcon } from '../../../common/assets/icons/location_blue_icon.svg';
import { useAppSelector } from '../../../common/hooks/reduxHooks';
import {
  OrganizationToSave,
  OrganizationWithOwner,
} from '../../../common/types/Organization';
import { UserRoles } from '../../../common/types/User';
import SnackbarUtils from '../../../common/utils/SnackbarUtilsConfigurator';
import OrganizationInput from '../OrganizationCreateScreen/components/OrganizationInput';

import OrganizationMembersTable from './components/OrganizationMembersTable';

const OrganizationEditSchema = Yup.object({
  name: Yup.string().required('Please enter an organization name').trim(),
  owner: Yup.string().required('Please select an owner'),
  address: Yup.string().required('Please enter an address').trim(),
  phone: Yup.string()
    .phone('US', false, 'Please enter a valid phone number')
    .required('Please enter a phone number')
    .trim(),
  priceDesktop: Yup.number()
    .min(0, 'The price must be 0 or higher')
    .required('Please enter a price for Desktop Licenses'),
  priceNoteScans: Yup.number()
    .min(0, 'The price must be 0 or higher')
    .required('Please enter a price for Note Scans'),
});

const billingNotificationBox = {
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
};

const OrganizationEditScreen: React.FC = () => {
  const [saveOrganization, { isLoading: saveIsLoading }] =
    useSaveOrganizationMutation();
  const [updateOrganization, { isLoading: updateIsLoading }] =
    useUpdateOrganizationMutation();
  const [getOrganization, { isLoading: getIsLoading }] =
    useGetOrganizationMutation();
  const [activateSubscriptionRequest, { isLoading: activateIsLoading }] =
    useActivateOrgSubscriptionMutation();

  const [billingInfo, setBillingInfo] = useState({
    hasBillingDetails: true,
    hasActiveSubscription: true,
  });

  const navigate = useNavigate();

  const { user } = useAppSelector((state) => state.model);
  const { userRole } = useUserRole();

  const { id: pathId } = useParams();

  const [organizationId, setOrganizationId] = useState<string>('');

  const formik = useFormik<Yup.InferType<typeof OrganizationEditSchema>>({
    initialValues: {
      address: '',
      name: '',
      owner: '',
      phone: '',
      priceDesktop: 0.99,
      priceNoteScans: 0.99,
    },
    validationSchema: OrganizationEditSchema,
    onSubmit: async (values) => {
      const castedValues = OrganizationEditSchema.cast(values);
      if (pathId === 'add') {
        saveOrganization(castedValues as OrganizationToSave)
          .unwrap()
          .then((res) => {
            SnackbarUtils.success(res.message);
            formik.resetForm();
          });
      } else {
        updateOrganization({
          id: organizationId,
          data: castedValues as OrganizationToSave,
        })
          .unwrap()
          .then((res) => SnackbarUtils.success(res.message));
      }
    },
  });

  const initOrganizationData = (data: OrganizationWithOwner) => {
    formik.setValues({
      address: data.address,
      name: data.name,
      owner: data.owner,
      phone: data.phone,
      priceDesktop: data.priceDesktop,
      priceNoteScans: data.priceNoteScans,
    });

    setBillingInfo({
      hasBillingDetails: data.hasBillingDetails,
      hasActiveSubscription: data.hasActiveSubscription,
    });
  };

  useEffect(() => {
    if (pathId) {
      setOrganizationId(pathId);

      if (
        userRole === UserRoles.ORG_OWNER &&
        user?.organizationId !== parseInt(pathId)
      ) {
        navigate('/organizations');
        return;
      }

      getOrganization({ id: pathId })
        .unwrap()
        .then((res) => initOrganizationData(res.organization));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate, getOrganization, pathId]);

  const activateSubscription = () => {
    activateSubscriptionRequest({ id: organizationId })
      .unwrap()
      .then((res) => {
        SnackbarUtils.success(res.message);

        getOrganization({ id: organizationId })
          .unwrap()
          .then((res) => initOrganizationData(res.organization));
      });
  };

  if (userRole !== UserRoles.SUPER_ADMIN && userRole !== UserRoles.ORG_OWNER) {
    return <></>;
  }

  return (
    <Box
      className='white-background min-height-100'
      style={{
        paddingLeft: '40px',
        paddingRight: '40px',
        paddingTop: '54px',
      }}
    >
      {userRole === UserRoles.SUPER_ADMIN && (
        <Button
          disabled={saveIsLoading || getIsLoading || updateIsLoading}
          sx={{
            marginBottom: '16px',
          }}
          onClick={() =>
            window.history.state && window.history.state.idx > 0
              ? navigate(-1)
              : navigate(Pages.organizations)
          }
          color={'darkBlue'}
          variant={'text'}
          startIcon={<ChevronLeftIconBlue />}
        >
          Back
        </Button>
      )}
      <Grid
        container
        spacing={8}
        direction={{ xs: 'column', md: 'row' }}
        alignItems='center'
        sx={{
          marginTop: '0px',
        }}
      >
        <Backdrop
          open={
            saveIsLoading ||
            getIsLoading ||
            updateIsLoading ||
            activateIsLoading ||
            !user
          }
          sx={{
            zIndex: 99,
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <CircularProgress color='secondary' size='128px' />
        </Backdrop>

        <Grid item xs={6} width='100%'>
          <Stack spacing={2}>
            <OrganizationInput
              formik={formik}
              formikFieldKey='name'
              title='Organization Name'
            />

            <OrganizationInput
              formik={formik}
              formikFieldKey='owner'
              title='Owner'
              disabled={true}
            />

            <OrganizationInput
              formik={formik}
              formikFieldKey='address'
              title='Address'
              startIcon={<LocationIcon />}
            />
            <OrganizationInput
              formik={formik}
              formikFieldKey='phone'
              title='Phone Number'
            />
          </Stack>
        </Grid>
        <Grid item xs={6} width='100%'>
          <Box
            sx={{
              backgroundColor: 'rgba(34, 123, 239, 0.05)',
              p: '40px',
              borderRadius: '16px',
            }}
          >
            <Stack spacing={2}>
              <Typography
                variant='h6'
                fontSize={18}
                marginBottom='24'
                fontWeight={700}
              >
                Pricing
              </Typography>
              {/* <OrganizationInput
                formik={formik}
                formikFieldKey="priceMobile"
                title="Price mobile license"
                isCurrency
                disabled={userRole !== UserRoles.SUPER_ADMIN}
              /> */}
              <OrganizationInput
                formik={formik}
                formikFieldKey='priceDesktop'
                title='Price desktop license'
                isCurrency
                disabled={userRole !== UserRoles.SUPER_ADMIN}
              />
              <OrganizationInput
                formik={formik}
                formikFieldKey='priceNoteScans'
                title='Price note scans'
                isCurrency
                disabled={userRole !== UserRoles.SUPER_ADMIN}
              />
            </Stack>
            {userRole === UserRoles.ORG_OWNER && (
              <Stack>
                <Button
                  onClick={() => openBillingPortal(user?.email)}
                  disableElevation
                  disabled={saveIsLoading || getIsLoading || updateIsLoading}
                  color='primary'
                  variant='contained'
                  sx={{
                    borderRadius: '50px',
                    height: '50px',
                    mt: '24px',
                    mb: '24px',
                  }}
                >
                  <Typography color='white'>
                    Access Stripe Customer Portal
                  </Typography>
                </Button>

                {!billingInfo.hasActiveSubscription &&
                  billingInfo.hasBillingDetails && (
                    <Box sx={billingNotificationBox}>
                      <Alert severity='error' color='error' variant='standard'>
                        Your billing details were added, but you do not have an
                        active subscription. To activate your subscription press
                        the button bellow:
                      </Alert>
                      <Button
                        onClick={activateSubscription}
                        variant='contained'
                        color='primary'
                        disableElevation
                        sx={{
                          borderRadius: '50px',
                          mt: '8px',
                        }}
                      >
                        Activate Subscription
                      </Button>
                    </Box>
                  )}

                {!billingInfo.hasActiveSubscription &&
                  !billingInfo.hasBillingDetails && (
                    <Box sx={billingNotificationBox}>
                      <Alert severity='error' color='error' variant='standard'>
                        You do not have an active subscription. Please open the
                        Stripe Customer Dashboard and add your billing details
                        and a payment method before being able to activate your
                        subscription. When that is done, refresh this page.
                      </Alert>
                    </Box>
                  )}
              </Stack>
            )}

            {userRole === UserRoles.SUPER_ADMIN && (
              <Stack sx={{ marginTop: '24px' }}>
                {!billingInfo.hasActiveSubscription &&
                  billingInfo.hasBillingDetails && (
                    <Box sx={billingNotificationBox}>
                      <Alert severity='error' color='error' variant='standard'>
                        This organization&apos;s billing details were added, but
                        there is no active subscription.
                      </Alert>
                    </Box>
                  )}

                {!billingInfo.hasActiveSubscription &&
                  !billingInfo.hasBillingDetails && (
                    <Box sx={billingNotificationBox}>
                      <Alert severity='error' color='error' variant='standard'>
                        This organization does not have an active subscription
                        or billing details.
                      </Alert>
                    </Box>
                  )}

                {billingInfo.hasActiveSubscription && (
                  <Box sx={billingNotificationBox}>
                    <Alert
                      severity='success'
                      color='success'
                      variant='standard'
                    >
                      This organization has an active subscription.
                    </Alert>
                  </Box>
                )}
              </Stack>
            )}
          </Box>
        </Grid>
        <Button
          onClick={() => formik.handleSubmit()}
          disableElevation
          disabled={saveIsLoading || getIsLoading || updateIsLoading}
          color='primary'
          variant='contained'
          sx={{
            borderRadius: '50px',
            height: '50px',
            width: '132px',
            mt: '48px',
            ml: '78px',
            mb: '40px',
          }}
        >
          <Typography color='white'>Save Changes</Typography>
        </Button>
      </Grid>
      {organizationId && (
        <OrganizationMembersTable organizationId={organizationId} />
      )}
    </Box>
  );
};

export default OrganizationEditScreen;
